import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { useMyGroups } from '@/features/groups/hooks/useGroups';
import { useDialogA11y } from '@/shared/hooks/useDialogA11y';

/**
 * Elegir a qué grupo va el gasto nuevo, cuando hay más de uno.
 *
 * Estaba dentro de `NewExpenseFAB`. Sale a su propio fichero porque desde que la
 * barra lateral de escritorio ofrece «Nuevo gasto» hay **dos** sitios que lo
 * abren, y tenerlo colgando del flotante obligaba a montar el flotante entero
 * para poder enseñarlo.
 */
export function GroupPickerSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: groups = [], isLoading } = useMyGroups();
  const active = groups.filter((g) => !g.is_archived);
  const titleId = useId();
  const panelRef = useDialogA11y(onClose);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40 lg:items-center lg:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        // Hoja por abajo en móvil, tarjeta centrada en escritorio: una hoja
        // pegada al borde inferior de una pantalla de 900 px de alto obliga a
        // bajar la vista al sitio más lejano del cursor.
        className="w-full rounded-t-2xl bg-white p-4 shadow-2xl lg:max-w-md lg:rounded-2xl"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 id={titleId} className="font-serif text-lg text-brand">
            {t('expenses.pick_group')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-brand"
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>
        {isLoading && <p className="text-muted text-sm">{t('common.loading')}</p>}
        {!isLoading && active.length === 0 && (
          <p className="text-muted text-sm">{t('expenses.no_groups_yet')}</p>
        )}
        <ul className="max-h-[60vh] space-y-1 overflow-y-auto">
          {active.map((g) => (
            <li key={g.id}>
              <button
                type="button"
                onClick={() => {
                  navigate(`/groups/${g.id}/expenses/new`);
                  onClose();
                }}
                className="flex w-full items-center gap-3 rounded border border-neutral-200 p-3 text-left transition-colors hover:border-brand"
              >
                <span className="text-2xl">{g.emoji ?? '📊'}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{g.name}</div>
                  <div className="text-muted text-xs">{g.default_currency}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
